import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LabourMobilizationService, LabourDailyReport, LabourDailyReportDetail } from '../../shared/services/labour-mobilization.service';
import { ToastrMsgService } from '../../shared/services/shared-services/toastr.service';
import { ApiService } from '../../shared/services/api.service';

@Component({
  selector: 'app-daily-report-form',
  templateUrl: './daily-report-form.component.html',
  styleUrls: ['./daily-report-form.component.css']
})
export class DailyReportFormComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  reportForm!: FormGroup;
  reportId: number | null = null;
  isEditMode = false;
  loading = false;
  projects: any[] = [];
  labourCategories: Array<{ key: string; label: string; visible: boolean }> = [];
  defaultCategories = [
    { key: 'mason', label: 'Mason', visible: true },
    { key: 'female_coolie', label: 'F/Cooli', visible: true },
    { key: 'male_coolie', label: 'M/Cooli', visible: true },
    { key: 'carpenter', label: 'Carpenter', visible: true },
    { key: 'fitter', label: 'Fitter', visible: true },
    { key: 'fitter_helper', label: 'Fitter Helper', visible: true },
    { key: 'surveyor', label: 'Surveyor', visible: true },
    { key: 'treader', label: 'Treader', visible: true },
    { key: 'others', label: 'Others', visible: true }
  ];
  
  showColumnConfig = false;
  showCategoryManager = false;
  newCategoryLabel = '';
  editingCategoryKey: string | null = null;
  editingCategoryLabel = '';

  shiftTypes = [
    { value: 'day', label: 'Day Shift' },
    { value: 'night', label: 'Night Shift' },
    { value: 'both', label: 'Both' }
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private labourService: LabourMobilizationService,
    private apiService: ApiService,
    private toastrMsgService: ToastrMsgService
  ) {
    this.loadCategories();
    this.initializeForm();
  }

  loadCategories(): void {
    // Load from localStorage or use defaults
    const savedCategories = localStorage.getItem('labour_categories');
    if (savedCategories) {
      try {
        this.labourCategories = JSON.parse(savedCategories);
      } catch (e) {
        this.labourCategories = [...this.defaultCategories];
      }
    } else {
      this.labourCategories = [...this.defaultCategories];
    }
  }

  saveCategories(): void {
    localStorage.setItem('labour_categories', JSON.stringify(this.labourCategories));
  }

  ngOnInit(): void {
    this.loadProjects();
    
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.reportId = +params['id'];
        this.isEditMode = true;
        this.loadReport();
      } else {
        // Add one empty row by default for new reports
        this.addDetailRow();
        const projectId = this.route.snapshot.queryParamMap.get('project_id');
        if (projectId) {
          this.reportForm.patchValue({ project_id: Number(projectId) || projectId });
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeForm(): void {
    this.reportForm = this.fb.group({
      project_id: ['', Validators.required],
      report_date: [new Date().toISOString().split('T')[0], Validators.required],
      contractor_name: [''],
      client_name: [''],
      shift_type: ['day', Validators.required],
      hindrance: [''],
      details: this.fb.array([])
    });
  }

  get detailsArray(): FormArray {
    return this.reportForm.get('details') as FormArray;
  }

  addDetailRow(): void {
    const formGroup: any = {
      sub_contractor_name: [''],
      notes: ['']
    };

    // Add form controls for each category dynamically
    this.labourCategories.forEach(cat => {
      formGroup[cat.key] = [0, [Validators.min(0)]];
    });

    const detailGroup = this.fb.group(formGroup);
    this.detailsArray.push(detailGroup);
  }

  removeDetailRow(index: number): void {
    this.detailsArray.removeAt(index);
  }

  copyDetailRow(index: number): void {
    const rowData = this.detailsArray.at(index).value;
    const formGroup: any = {
      sub_contractor_name: [rowData.sub_contractor_name || ''],
      notes: [rowData.notes || '']
    };

    // Copy values for each category dynamically
    this.labourCategories.forEach(cat => {
      formGroup[cat.key] = [rowData[cat.key] || 0];
    });

    const newRow = this.fb.group(formGroup);
    this.detailsArray.insert(index + 1, newRow);
  }

  getVisibleColumns(): any[] {
    return this.labourCategories.filter(cat => cat.visible);
  }

  toggleColumnVisibility(key: string): void {
    const category = this.labourCategories.find(cat => cat.key === key);
    if (category) {
      category.visible = !category.visible;
    }
  }

  calculateRowTotal(detailIndex: number): number {
    const detailGroup = this.detailsArray.at(detailIndex);
    const values = detailGroup.value;
    let total = 0;
    
    // Calculate total using dynamic categories
    this.labourCategories.forEach(cat => {
      total += (values[cat.key] || 0);
    });
    
    return total;
  }

  calculateGrandTotal(): number {
    let total = 0;
    for (let i = 0; i < this.detailsArray.length; i++) {
      total += this.calculateRowTotal(i);
    }
    return total;
  }

  loadProjects(): void {
    this.apiService.getProjectCommonAll({ limit: 1000 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.projects = res.data || res || [];
        },
        error: (err) => {
          console.error('Error loading projects:', err);
        }
      });
  }

  loadReport(): void {
    if (!this.reportId) return;

    this.loading = true;
    this.labourService.getDailyReportById(this.reportId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const report = response.data;
          this.reportForm.patchValue({
            project_id: report.project_id,
            report_date: report.report_date,
            contractor_name: report.contractor_name || '',
            client_name: report.client_name || '',
            shift_type: report.shift_type || 'day',
            hindrance: report.hindrance || ''
          });

          // Clear existing details and populate from report
          while (this.detailsArray.length !== 0) {
            this.detailsArray.removeAt(0);
          }

          if (report.details && report.details.length > 0) {
            report.details.forEach((detail: LabourDailyReportDetail) => {
              const formGroup: any = {
                sub_contractor_name: [detail.sub_contractor_name || ''],
                notes: [detail.notes || '']
              };

              // Load values for each category dynamically
              this.labourCategories.forEach(cat => {
                formGroup[cat.key] = [(detail as any)[cat.key] || 0];
              });

              const detailGroup = this.fb.group(formGroup);
              this.detailsArray.push(detailGroup);
            });
          } else {
            // Add one empty row if no details exist
            this.addDetailRow();
          }

          this.loading = false;
        },
        error: (err) => {
          console.error('Error loading report:', err);
          this.toastrMsgService.error(err.error?.message || 'Failed to load report');
          this.loading = false;
        }
      });
  }

  onSubmit(): void {
    if (this.reportForm.valid) {
      this.loading = true;
      const formValue = this.reportForm.value;

      const reportData: any = {
        project_id: formValue.project_id,
        report_date: formValue.report_date,
        contractor_name: formValue.contractor_name,
        client_name: formValue.client_name,
        shift_type: formValue.shift_type,
        hindrance: formValue.hindrance,
        details: formValue.details.map((detail: any) => {
          const detailObj: any = {
            sub_contractor_name: detail.sub_contractor_name,
            notes: detail.notes
          };

          // Add all category values dynamically
          this.labourCategories.forEach(cat => {
            detailObj[cat.key] = detail[cat.key] || 0;
          });

          return detailObj;
        })
      };

      const operation = this.isEditMode
        ? this.labourService.updateDailyReport(this.reportId!, reportData)
        : this.labourService.createDailyReport(reportData);

      operation.pipe(takeUntil(this.destroy$)).subscribe({
        next: (response) => {
          this.toastrMsgService.success(
            this.isEditMode ? 'Report updated successfully' : 'Report created successfully'
          );
          this.router.navigate(['/home/labour-mobilization/daily-reports']);
        },
        error: (err) => {
          console.error('Error saving report:', err);
          this.toastrMsgService.error(err.error?.message || 'Failed to save report');
          this.loading = false;
        }
      });
    } else {
      this.markFormGroupTouched(this.reportForm);
      this.toastrMsgService.error('Please fill all required fields');
    }
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormArray) {
        control.controls.forEach((ctrl: any) => {
          if (ctrl instanceof FormGroup) {
            this.markFormGroupTouched(ctrl);
          } else {
            ctrl.markAsTouched();
          }
        });
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/home/labour-mobilization/daily-reports']);
  }

  getSubTotal(category: string): number {
    let total = 0;
    for (let i = 0; i < this.detailsArray.length; i++) {
      const value = this.detailsArray.at(i).get(category)?.value || 0;
      total += value;
    }
    return total;
  }

  // Category Management Methods
  addCategory(): void {
    if (!this.newCategoryLabel || this.newCategoryLabel.trim() === '') {
      this.toastrMsgService.error('Please enter a category label');
      return;
    }

    const newKey = this.newCategoryLabel.toLowerCase().replace(/[^a-z0-9]/g, '_');
    
    // Check if key already exists
    if (this.labourCategories.some(cat => cat.key === newKey)) {
      this.toastrMsgService.error('Category with this name already exists');
      return;
    }

    // Add new category
    this.labourCategories.push({
      key: newKey,
      label: this.newCategoryLabel.trim(),
      visible: true
    });

    this.saveCategories();
    this.newCategoryLabel = '';
    this.toastrMsgService.success('Category added successfully');
    
    // Rebuild form arrays with new category
    this.rebuildFormArrays();
  }

  removeCategory(key: string): void {
    if (this.labourCategories.length <= 1) {
      this.toastrMsgService.error('At least one category is required');
      return;
    }

    const index = this.labourCategories.findIndex(cat => cat.key === key);
    if (index > -1) {
      this.labourCategories.splice(index, 1);
      this.saveCategories();
      this.toastrMsgService.success('Category removed successfully');
      
      // Rebuild form arrays without removed category
      this.rebuildFormArrays();
    }
  }

  startEditCategory(key: string): void {
    const category = this.labourCategories.find(cat => cat.key === key);
    if (category) {
      this.editingCategoryKey = key;
      this.editingCategoryLabel = category.label;
    }
  }

  saveCategoryEdit(): void {
    if (!this.editingCategoryKey || !this.editingCategoryLabel || this.editingCategoryLabel.trim() === '') {
      return;
    }

    const category = this.labourCategories.find(cat => cat.key === this.editingCategoryKey);
    if (category) {
      category.label = this.editingCategoryLabel.trim();
      this.saveCategories();
      this.cancelCategoryEdit();
      this.toastrMsgService.success('Category updated successfully');
    }
  }

  cancelCategoryEdit(): void {
    this.editingCategoryKey = null;
    this.editingCategoryLabel = '';
  }

  rebuildFormArrays(): void {
    // Get current row data
    const currentData = this.detailsArray.controls.map(control => control.value);
    
    // Clear and rebuild form array
    while (this.detailsArray.length !== 0) {
      this.detailsArray.removeAt(0);
    }

    // Recreate rows with new category structure
    currentData.forEach(data => {
      const formGroup: any = {
        sub_contractor_name: [data.sub_contractor_name || ''],
        notes: [data.notes || '']
      };

      this.labourCategories.forEach(cat => {
        formGroup[cat.key] = [data[cat.key] || 0];
      });

      this.detailsArray.push(this.fb.group(formGroup));
    });

    // If no rows, add one empty row
    if (this.detailsArray.length === 0) {
      this.addDetailRow();
    }
  }
}

